import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, Renderer2, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Subscription, interval, timer } from 'rxjs';
import { debounce } from 'rxjs/operators';

import { ApContentService, ApHeadlessContentService, ContentPrefix } from '@ap/content';
import { ConditionParameters, MijnConditiesService } from '@ap/mijn-condities';
import { ModalContent } from '@ap/modal';
import { ZelfRegelenDetails } from '@ap/interface-apix-mijn-overzicht';

import { AnalyticsService } from './analytics/analytics.service';
import { XC_HEADLESS_SEARCH_CONTENT } from './mijn-zoeken.constants';
import content from './mijn-zoeken.content.json';
import { ResultItem, SearchOptions, SearchResult, SearchVariant } from './mijn-zoeken.model';
import { MijnZoekenService } from './mijn-zoeken.service';

/**
 * @module MijnZoekenModule
 * @ngdoc component
 * @name MijnZoekenComponent
 */
@Component({
    selector: 'mijn-zoeken',
    templateUrl: './mijn-zoeken.component.html',
    styleUrls: ['./mijn-zoeken.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class MijnZoekenComponent extends ModalContent<SearchOptions> implements OnInit, OnDestroy, AfterViewInit {
    @Input()
    public variant = SearchVariant.DEFAULT;

    @Input()
    public isBusiness = false;

    @Output()
    public resultClicked: EventEmitter<ResultItem> = new EventEmitter();

    public searchControl: FormControl;
    public searchTerm: string;
    public searchVariant = SearchVariant.DEFAULT;
    public showResults: boolean;
    public recentResults: SearchResult[];
    public searchResults: SearchResult[];
    public searchResultsSug: SearchResult[];
    public isBusinessClient: boolean;

    private readonly maxScore = 0.3;
    private readonly maxSugScore = 0.6;
    private readonly debounceInterval = 400;
    private readonly analyticsInterval = 2000;
    private readonly minTermLength = 2;
    private readonly subscriptions = new Subscription();

    private readonly MAX_SEARCH_RESULTS = 5;
    private readonly MAX_SEARCH_SUGGESTIONS = 3;

    private analyticsTimer: Subscription;
    private conditionParameters: ConditionParameters;

    public constructor(
        private readonly contentService: ApContentService,
        private readonly analyticService: AnalyticsService,
        private readonly apHeadlessContentService: ApHeadlessContentService,
        private readonly mijnConditiesService: MijnConditiesService,
        private readonly renderer: Renderer2,
        private readonly searchService: MijnZoekenService
    ) {
        super();
    }

    public ngOnInit(): void {
        this.contentService.addContentItems(content);
        this.searchControl = new FormControl();

        if (this.modalOptions) {
            this.searchVariant = this.modalOptions.options.variant;
            this.isBusinessClient = this.modalOptions.options.isBusiness;
        } else {
            this.isBusinessClient = this.isBusiness;
        }
        if (this.isBusinessClient) {
            this.contentService.setContentPrefix(ContentPrefix.BUSINESS);
            this.searchService.setIsBusiness(true);
        }

        this.subscriptions.add(
            this.mijnConditiesService.initializeConditionParameters().subscribe((mijnCondities: ConditionParameters) => {
                this.conditionParameters = mijnCondities;
            })
        );
        this.initializeSearchContent();
        this.subscriptions.add(
            this.apHeadlessContentService.getContentItems(XC_HEADLESS_SEARCH_CONTENT).subscribe((result) => {
                if (result) {
                    this.initializeSearchContent(result);
                }
            })
        );

        this.subscriptions.add(
            this.searchControl.valueChanges.pipe(debounce(() => interval(this.debounceInterval))).subscribe((value) => {
                this.delayAnalyticsEvent();
                if (value && value.length > this.minTermLength) {
                    this.doSearch(value);
                } else {
                    this.searchTerm = null;
                }
            })
        );
    }

    public ngAfterViewInit(): void {
        if (this.searchVariant === SearchVariant.MODAL) {
            const inputElement = this.renderer.selectRootElement('mijn-zoeken input');
            setTimeout(() => inputElement.focus(), 100);
        }
    }

    public ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    public doSearch(searchTerm: string): void {
        this.searchTerm = searchTerm;
        this.delayAnalyticsEvent();
        const allResults = this.searchService.getSearchResults(searchTerm);

        this.searchResults = allResults.filter((match) => match.score < this.maxScore).slice(0, this.MAX_SEARCH_RESULTS);

        this.analyticService.pushSearchResultAmountAnalytics(searchTerm, this.searchResults.length);

        this.searchResultsSug = allResults
            .filter((match) => match.score >= this.maxScore && match.score < this.maxSugScore)
            .slice(0, this.MAX_SEARCH_SUGGESTIONS);

        this.showResults = true;
    }

    public delayAnalyticsEvent(): void {
        if (this.analyticsTimer && !this.analyticsTimer.closed) {
            this.analyticsTimer.unsubscribe();
        }
        this.analyticsTimer = timer(this.analyticsInterval).subscribe(() => this.sendAnalyticsEvent());
    }

    public sendAnalyticsEvent() {
        this.analyticsTimer.unsubscribe();
        if (this.searchTerm != null && this.searchTerm !== '') {
            this.analyticService.pushSearchTermAnalytics(this.searchTerm);
        }
    }

    public doClear(): void {
        this.showResults = false;
        if (this.searchVariant === SearchVariant.MODAL) {
            this.decline();
        }
    }

    public openLink(item: SearchResult): void {
        let resultItem = {
            product: item.name.substring(item.name.lastIndexOf('.') + 1),
            path: item.link,
            routeKey: item.routeKey
        };

        if (resultItem.path) {
            this.addPathParametersToLink(resultItem);
        }

        this.showResults = false;
        if (this.searchVariant === SearchVariant.MODAL) {
            this.accept(resultItem);
        } else {
            this.resultClicked.emit(resultItem);
        }
    }

    private addPathParametersToLink(resultItem) {
        if (resultItem.path.includes('${')) {
            let t: keyof ZelfRegelenDetails;
            let possibleParams: { [key: string]: string } = {};
            for (t in this.conditionParameters.zelfRegelenDetails) {
                possibleParams[t] = String(this.conditionParameters.zelfRegelenDetails[t]);
            }
            resultItem.path = this.contentService.interpolate(resultItem.path, possibleParams);
        }
        return resultItem;
    }

    private initializeSearchContent(contentItems?: { [key: string]: string }): void {
        contentItems == null ? this.searchService.setUpSearch() : this.searchService.setUpSearch(contentItems);
        this.loadRecents();
    }

    private loadRecents() {
        this.recentResults = [
            {
                actionType: 'internalLink',
                routeKey: this.getRouteKey(`mijn.zoeken.search.suggestion.item1.routeKey`),
                link: this.contentService.get(`mijn.zoeken.search.suggestion.item1.link`),
                name: `search.suggestion.item1`,
                score: null,
                subTitle: this.contentService.get(`mijn.zoeken.search.suggestion.item1.subtitle`),
                title: this.contentService.get(`mijn.zoeken.search.suggestion.item1.title`)
            },
            {
                actionType: 'internalLink',
                routeKey: this.getRouteKey(`mijn.zoeken.search.suggestion.item2.routeKey`),
                link: this.contentService.get(`mijn.zoeken.search.suggestion.item2.link`),
                name: `search.suggestion.item2`,
                score: null,
                subTitle: this.contentService.get(`mijn.zoeken.search.suggestion.item2.subtitle`),
                title: this.contentService.get(`mijn.zoeken.search.suggestion.item2.title`)
            },
            {
                actionType: 'internalLink',
                routeKey: this.getRouteKey(`mijn.zoeken.search.suggestion.item3.routeKey`),
                link: this.contentService.get(`mijn.zoeken.search.suggestion.item3.link`),
                name: `search.suggestion.item3`,
                score: null,
                subTitle: this.contentService.get(`mijn.zoeken.search.suggestion.item3.subtitle`),
                title: this.contentService.get(`mijn.zoeken.search.suggestion.item3.title`)
            },
            {
                actionType: 'internalLink',
                routeKey: this.getRouteKey(`mijn.zoeken.search.suggestion.item4.routeKey`),
                link: this.contentService.get(`mijn.zoeken.search.suggestion.item4.link`),
                name: `search.suggestion.item4`,
                score: null,
                subTitle: this.contentService.get(`mijn.zoeken.search.suggestion.item4.subtitle`),
                title: this.contentService.get(`mijn.zoeken.search.suggestion.item4.title`)
            }
        ];
        this.removeEmptyTitleObjects(this.recentResults);
    }

    private removeEmptyTitleObjects(objectsArray) {
        this.recentResults = objectsArray.filter((item) => item.title);
    }

    private getRouteKey(contentKey): string {
        const routeKey = this.contentService.get(contentKey);
        return routeKey === contentKey ? null : routeKey;
    }
}
